import { Button, Checkbox, Modal, Select } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { DashboardFilterType } from 'views/Dashboard/ImplementData/components/Filter/useFilter';
import { useOrgConfigQAEnable, useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import useDownload from './useDownload';
import FlexStyled from 'components/styled/flex';
import {
  mercadoNewDataDate,
  usePlatformSelect,
  useStoreInfoSelect,
  useStoreTypeSelect,
} from 'views/Dashboard/ImplementData/components/Filter/Provider';
import dayjs, { Dayjs } from 'lib/dayjs';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import { DashboardPlatformSelectorFeature, DashboardStoreSelectorFeature } from 'utils/featureFlag';
import { currencyOptions } from 'views/Dashboard/ImplementData/components/Filter/CurrencySelector';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import { DatePicker } from 'components/DatePicker';

interface Props {
  defaultValue:
    | 'HUMAN_MACHINE_COMBINATION'
    | 'ROBOT_INDEPENDENT'
    | 'ROBOT_PARTICIPATE'
    | 'ORDER_REMINDER'
    | 'PAYMENT_REMINDER'
    | 'CANCEL_ORDER_REMINDER'
    | 'PRAISE_REMINDER';
  initialValue: DashboardFilterType;
}

export const conversationTypeMap = {
  HUMAN_MACHINE_COMBINATION: 1,
  ROBOT_INDEPENDENT: 3,
  ROBOT_PARTICIPATE: 2,
} as const;

export const ExportData = (props: Props) => {
  const { defaultValue, initialValue } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const orgConfig = useOrgConfigStateValue();
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const { currency } = useDashboardCurrency();
  const qaEnable = useOrgConfigQAEnable();

  const [filter, setFilter] = useState({
    ...initialValue,
    currency,
  });

  const { download, downloading } = useDownload();

  const optionsRow1 = [
    { label: t('ViewDashboard.人机结合'), value: 'HUMAN_MACHINE_COMBINATION' },
    { label: t('ViewDashboard.机器人参与'), value: 'ROBOT_PARTICIPATE' },
  ];
  if (orgConfig.robotIndependentEnable) {
    optionsRow1.push({ label: t('ViewDashboard.机器人独立'), value: 'ROBOT_INDEPENDENT' });
  }

  const optionsRow2 = [
    { label: t('机器人催单贡献'), value: 'ORDER_REMINDER' },
    { label: t('机器人催付贡献'), value: 'PAYMENT_REMINDER' },
    { label: t('机器人取消订单挽回贡献'), value: 'CANCEL_ORDER_REMINDER' },
    { label: t('机器人催好评贡献'), value: 'PRAISE_REMINDER', disabled: true },
  ];

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments: filter.departments,
    scene: 'ASSISTANT_PERFORMANCE',
    dashboardName: 'PerformanceRobot',
    showPlatformAll: true,
    value: filter.platform,
    onChange: (value) => {
      setFilter((pre) => {
        const dateRange = pre.dateRange;
        const isMercadoFlag =
          value === 'MERCADO' &&
          dateRange &&
          dateRange[0].isBefore(mercadoNewDataDate, 'days') &&
          dateRange[1].isSameOrAfter(mercadoNewDataDate, 'days');

        return {
          ...pre,
          platform: value,
          storeInfo: {},
          dateRange: isMercadoFlag ? [dayjs(mercadoNewDataDate), dateRange[1]] : dateRange,
        };
      });
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: filter.storeType,
    onChange: (storeType) => {
      setFilter((pre) => ({
        ...pre,
        storeType,
        storeInfo: {},
      }));
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform: filter.platform,
    departments: filter.departments,
    platformOptions,
    storeType: filter.storeType,
    storeInfo: filter.storeInfo,
    onChange: (storeInfo) =>
      setFilter((pre) => ({
        ...pre,
        storeInfo,
      })),
    scene: 'ASSISTANT_PERFORMANCE',
  });

  const allValues = [...optionsRow1, ...optionsRow2]
    .filter((_: any) => !_.disabled)
    .map((opt) => opt.value);
  const indeterminate = checkedList.length > 0 && checkedList.length < allValues.length;
  const checkAll = checkedList.length === allValues.length;

  const handleExport = () => {
    if (checkedList.includes('HUMAN_MACHINE_COMBINATION')) {
      download('CONVERSATION', filter, conversationTypeMap.HUMAN_MACHINE_COMBINATION);
    }
    if (checkedList.includes('ROBOT_INDEPENDENT')) {
      download('CONVERSATION', filter, conversationTypeMap.ROBOT_INDEPENDENT);
    }
    if (checkedList.includes('ROBOT_PARTICIPATE')) {
      download('CONVERSATION', filter, conversationTypeMap.ROBOT_PARTICIPATE);
    }
    if (checkedList.includes('ORDER_REMINDER')) {
      download('ORDER_REMINDER', filter);
    }
    if (checkedList.includes('PAYMENT_REMINDER')) {
      download('PAYMENT_REMINDER', filter);
    }
    if (checkedList.includes('CANCEL_ORDER_REMINDER')) {
      download('CANCEL_ORDER_REMINDER', filter);
    }
    if (checkedList.includes('PRAISE_REMINDER')) {
      download('PRAISE_REMINDER', filter);
    }
  };

  useEffect(() => {
    if (visible) {
      setCheckedList([defaultValue]);
      setFilter({
        ...initialValue,
        currency,
      });
    }
  }, [visible, defaultValue, currency, initialValue]);

  const modal = (
    <Modal
      width={720}
      visible={visible}
      onCancel={() => setVisible(false)}
      title={t('ViewDashboard.导出详细数据')}
      footer={
        <FlexStyled.Flex className="justify-end">
          <span className="mr-4">
            {t('export_pref_modal_selected_x', { x: checkedList?.length ?? 0 })}
          </span>
          <Button type="default" disabled={downloading} onClick={() => setVisible(false)}>
            {t('translation.取消')}
          </Button>
          <Button
            type="primary"
            onClick={handleExport}
            loading={downloading}
            disabled={!checkedList?.length}
          >
            {t('export_pref_modal_button_export')}
          </Button>
        </FlexStyled.Flex>
      }
    >
      <div className="flex flex-col gap-2 border border-[var(--fett-border-default)] border-solid rounded-md">
        <div className="flex items-center border-t-0 border-l-0 border-r-0 border-b border-[var(--fett-border-default)] border-solid px-2 py-3 gap-4 flex-wrap">
          <DepartmentsSelect
            dashboardType="order"
            value={filter.departments}
            onChange={(departments) => {
              setFilter((pre) => ({
                ...pre,
                departments,
                platform: undefined,
                storeInfo: {},
              }));
            }}
          />
          {DashboardPlatformSelectorFeature && platformSelect}
          {DashboardStoreSelectorFeature && storeTypeSelect}
          {storeInfoSelect}
          <Select
            value={filter.currency?.region}
            options={currencyOptions}
            onChange={(value) => {
              const currency = currencyOptions.find((_) => _.region === value);
              setFilter((pre) => ({
                ...pre,
                currency: currency!,
              }));
            }}
          />
        </div>
        <div className="flex flex-row gap-2 px-2 py-3 items-center">
          <span>{t('ViewDashboard.日期')}</span>
          <DatePicker.RangePicker
            className="w-[380px]"
            disabledDate={(current) => {
              return current && (current < dayjs().subtract(1, 'year') || current > dayjs());
            }}
            value={filter.dateRange}
            onChange={(dateRange) => {
              setFilter((pre) => ({ ...pre, dateRange: dateRange as [Dayjs, Dayjs] }));
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 border border-[var(--fett-border-default)] border-solid rounded-md mt-4">
        <div className="flex items-center border-t-0 border-l-0 border-r-0 border-b border-[var(--fett-border-default)] border-solid px-2 py-3">
          <Checkbox
            indeterminate={indeterminate}
            onChange={(e) => {
              setCheckedList(e.target.checked ? allValues : []);
            }}
            checked={checkAll}
          >
            {t('全部数据')}
          </Checkbox>
        </div>
        <div className="flex flex-row gap-2 px-4 py-3">
          <Checkbox.Group
            value={checkedList}
            onChange={(value) => setCheckedList(value as string[])}
          >
            {qaEnable && (
              <div className="mb-2">
                {optionsRow1.map((opt) => (
                  <Checkbox key={opt.value} value={opt.value}>
                    <span>{opt.label}</span>
                  </Checkbox>
                ))}
              </div>
            )}
            <div>
              {optionsRow2.map((opt) => (
                <Checkbox key={opt.value} value={opt.value} disabled={opt.disabled}>
                  <span>{opt.label}</span>
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      <Button type="ghost" icon={<DownloadOutlined />} onClick={() => setVisible(true)}>
        {t('ViewDashboard.导出详细数据')}
      </Button>
      {modal}
    </>
  );
};
